import Splide from '@splidejs/splide';
import {stripSplideSlideRoleAttributes} from './splideAccessibility';

const SELECTOR = '.video-slider__splide';
const initialised = new WeakSet<HTMLElement>();
const instances = new WeakMap<HTMLElement, Splide>();

function initVideoSliderElement(element: HTMLElement): void {
    if (initialised.has(element)) return;
    initialised.add(element);

    const splide = new Splide(element, {
        label: element.dataset.splideLabel || '',
        type: 'slide',
        fixedWidth: 332,
        perMove: 1,
        gap: '14px',
        drag: true,
        touch: true,
        snap: true,
        arrows: false,
        pagination: false,
        mediaQuery: 'min',
        breakpoints: {
            768: {
                fixedWidth: 472,
                gap: '24px',
            },
            992: {
                fixedWidth: 912,
            },
        },
    }).mount();

    stripSplideSlideRoleAttributes(element);

    instances.set(element, splide);

    const root = element.closest('.video-slider');
    if (!root) return;

    const arrowContainer = root.querySelector<HTMLElement>('.video-slider__arrows');
    const prevBtn = root.querySelector<HTMLButtonElement>('.video-slider__arrow--prev');
    const nextBtn = root.querySelector<HTMLButtonElement>('.video-slider__arrow--next');

    prevBtn?.addEventListener('click', () => splide.go('<'));
    nextBtn?.addEventListener('click', () => splide.go('>'));

    function getVisibleCount(): number {
        return element.querySelectorAll('.splide__slide:not([aria-hidden="true"])').length || 1;
    }

    function updateArrowState(): void {
        const visible = getVisibleCount();
        if (arrowContainer) arrowContainer.hidden = splide.length <= visible;
        if (prevBtn) prevBtn.disabled = splide.index === 0;
        if (nextBtn) nextBtn.disabled = splide.index + visible >= splide.length;
    }

    splide.on('mounted move resize', updateArrowState);

    const liveRegion = document.createElement('div');
    liveRegion.className = 'visually-hidden';
    liveRegion.setAttribute('aria-live', 'polite');
    liveRegion.setAttribute('aria-atomic', 'true');
    root.appendChild(liveRegion);

    splide.on('moved', (newIndex) => {
        liveRegion.textContent = `${newIndex + 1} von ${splide.length}`;
    });
}

export function initVideoSlider(): void {
    document.querySelectorAll<HTMLElement>(SELECTOR).forEach(initVideoSliderElement);

    if (!document.body.classList.contains('neos-backend')) return;

    new MutationObserver((mutations) => {
        for (const mutation of mutations) {
            for (const node of mutation.addedNodes) {
                if (!(node instanceof HTMLElement)) continue;
                if (node.matches(SELECTOR)) {
                    initVideoSliderElement(node);
                }
                node.querySelectorAll<HTMLElement>(SELECTOR).forEach(initVideoSliderElement);
                if (node.matches('.video-slider__slide')) {
                    const sliderEl = node.closest<HTMLElement>(SELECTOR);
                    if (sliderEl) {
                        instances.get(sliderEl)?.refresh();
                    }
                }
            }
        }
    }).observe(document.body, { childList: true, subtree: true });
}
